import { Badge, cn } from "@simplicity/ui";
import { ChangeActionBadge } from "./OnsiteBadges";
import { formatPlanValue, isDestructive, planLogUrl, startsVm } from "./onsiteModel";
import type { OnsiteRun, PlanAction, PlanChange, PlanSummary, PlanValue } from "./types";

// The agent reports at most this many field names per change (onsitePlanDetail.ts).
const FIELD_CAP = 40;

function ActionBadge({ action }: { action: PlanAction }) {
  if (action === "import") return <Badge variant="outline">import</Badge>;
  return <ChangeActionBadge action={action} />;
}

function ChangedFields({ fields }: { fields: string[] }) {
  return (
    <p className="break-all text-muted-foreground">
      Fields: {fields.join(", ")}
      {fields.length >= FIELD_CAP && ` (the first ${FIELD_CAP}; there may be more)`}
    </p>
  );
}

function ValueRow({ value }: { value: PlanValue }) {
  const warn = startsVm(value);
  return (
    <tr data-warning={warn ? "true" : undefined} className={cn(warn && "text-warning")}>
      <th scope="row" className="pr-3 text-left font-normal text-muted-foreground">
        {value.field}
      </th>
      <td className="break-all pr-3">{formatPlanValue(value.before)}</td>
      <td className="break-all">
        → {value.afterUnknown ? "(known after apply)" : formatPlanValue(value.after)}
      </td>
    </tr>
  );
}

function ValueTable({ values }: { values: PlanValue[] }) {
  return (
    <table aria-label="Value changes">
      <thead className="text-muted-foreground">
        <tr>
          <th scope="col" className="pr-3 text-left font-normal">
            Field
          </th>
          <th scope="col" className="pr-3 text-left font-normal">
            Before
          </th>
          <th scope="col" className="text-left font-normal">
            After
          </th>
        </tr>
      </thead>
      <tbody>
        {values.map((value) => (
          <ValueRow key={value.field} value={value} />
        ))}
      </tbody>
    </table>
  );
}

function ChangeDetail({ change }: { change: PlanChange }) {
  const { reason, changedFields, values } = change;
  return (
    <>
      {reason && <p className="font-sans">Reason: {reason.replace(/_/g, " ")}</p>}
      {changedFields && <ChangedFields fields={changedFields} />}
      {values && <ValueTable values={values} />}
      {values?.some(startsVm) && (
        <p
          role="note"
          className="rounded border border-warning/40 bg-warning/10 px-2 py-1 font-sans text-warning"
        >
          Starts this VM if it is stopped.
        </p>
      )}
    </>
  );
}

function ChangeRow({ change }: { change: PlanChange }) {
  const destructive = isDestructive(change.action);
  return (
    <li
      data-destructive={destructive ? "true" : undefined}
      className={cn(
        "space-y-1 rounded px-2 py-1",
        destructive && "bg-destructive/10 text-destructive",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <ActionBadge action={change.action} />
        {change.importing && <ActionBadge action="import" />}
        <span className={cn("break-all", destructive && "font-semibold")}>{change.address}</span>
      </div>
      <ChangeDetail change={change} />
    </li>
  );
}

export function PlanChanges({ plan }: { plan: PlanSummary }) {
  if (plan.changes.length === 0) {
    return <p className="text-sm text-muted-foreground">No changes.</p>;
  }
  return (
    <ul className="space-y-1 font-mono text-xs" aria-label="Planned changes">
      {plan.changes.map((change) => (
        <ChangeRow key={`${change.action}:${change.address}`} change={change} />
      ))}
    </ul>
  );
}

/** The run's full Terraform output stays in the client's project; this links to it. */
export function PlanLogLink({ run }: { run: OnsiteRun }) {
  const url = planLogUrl(run);
  if (!url) return null;
  return (
    <div className="space-y-1">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-primary underline-offset-4 hover:underline"
      >
        Full {run.action} log
      </a>
      <p className="text-xs text-muted-foreground">
        Opens the agent&apos;s log in the client&apos;s project. You need to be signed in as a user
        with access to it.
      </p>
    </div>
  );
}
